import classNames from 'classnames'
import styles from './CopyText.module.css'
import { type CopyTextProps, CopyTextState } from './types'
import { LayoutCluster } from '../layouts'
import { TextInput } from '../TextInput'
import { copyToClipboard, delay } from '@betterplace/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'

function CopyText({
  textToCopy,
  showPreview = false,
  previewLabel,
  previewHint,
  children,
  onTextCopied,
  onError,
  className,
  fadeOutDelay = 3000,
  idleDelay = 200,
}: CopyTextProps) {
  const [state, setState] = useState<CopyTextState>(CopyTextState.Idle)

  const setStateWithDelay = useCallback(async () => {
    await delay(fadeOutDelay)
    setState(CopyTextState.FadingOut)
    await delay(idleDelay)
    setState(CopyTextState.Idle)
  }, [fadeOutDelay, idleDelay])

  useEffect(() => {
    if (state !== CopyTextState.Success && state !== CopyTextState.Error) return
    void setStateWithDelay().then().catch(console.error)
  }, [setStateWithDelay, state])

  const copyText = useCallback(() => {
    if (!textToCopy) return
    void copyToClipboard(textToCopy)
      .then(() => {
        onTextCopied?.(textToCopy)
        setState(CopyTextState.Success)
      })
      .catch((error) => {
        setState(CopyTextState.Error)
        onError?.(error as Error)
      })
  }, [textToCopy, onTextCopied, onError])

  const memoised = useMemo(() => children(copyText, state), [children, copyText, state])
  if (showPreview)
    return (
      <LayoutCluster className={classNames(styles.copyTextContainer, className)}>
        <TextInput
          className={styles.copyTextInput}
          readOnly
          label={previewLabel}
          description={previewHint}
          value={textToCopy as string}
        />
        {memoised}
      </LayoutCluster>
    )
  return memoised
}

export default CopyText
